import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import API_BASE_URL from '../../../../Constants';

/**
 * The modal that fetches/paginates buildings and calls onSelect(buildingID, agencyID)
 * when user clicks "Select".
 */
const BuildingLookupModal = ({ open, onClose, onSelect }) => {
  const [buildings, setBuildings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0); // 0-based
  const [totalPages, setTotalPages] = useState(1);

  // Fetch buildings for page p
  const fetchBuildings = async (p = 0) => {
    setIsLoading(true);
    try {
      const url = `${API_BASE_URL}/api/setup/buildings?page=${p}&size=10`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`Failed to fetch buildings. Status: ${res.status}`);
      }
      const data = await res.json();

      // assume shape: { content: [...], number, totalPages, ... }
      const arr = Array.isArray(data.content) ? data.content : [];
      setBuildings(arr);
      setPage(data.number || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Error fetching buildings in modal:', err);
      setBuildings([]);
    } finally {
      setIsLoading(false);
    }
  };

  // When modal opens => fetch first page
  useEffect(() => {
    if (open) {
      setPage(0);
      fetchBuildings(0);
    }
  }, [open]);

  // Next/Prev
  const handleNext = () => {
    if (page < totalPages - 1) {
      fetchBuildings(page + 1);
    }
  };
  const handlePrev = () => {
    if (page > 0) {
      fetchBuildings(page - 1);
    }
  };

  // Send buildingID & agencyID back to parent
  const handleSelectBuilding = (bldg) => {
    const bId = bldg.buildingID;
    const aId = bldg.agencyID ?? '';
    onSelect?.(bId, aId);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Select a Building</DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <p>Loading buildings...</p>
        ) : (
          <>
            {/* Table of buildings */}
            <table className="min-w-full border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border p-2 text-left">Building ID</th>
                  <th className="border p-2 text-left">Reference</th>
                  <th className="border p-2 text-left">Agency ID</th>
                  <th className="border p-2 text-left"></th>
                </tr>
              </thead>
              <tbody>
                {buildings.map((b) => {
                  const bId = b.buildingID;
                  const ref = b.buildingReference?.trim() || 'Unknown';
                  const agId = b.agencyID ?? '';
                  return (
                    <tr key={bId} className="hover:bg-gray-50">
                      <td className="border p-2">{bId}</td>
                      <td className="border p-2">{ref}</td>
                      <td className="border p-2">{agId}</td>
                      <td className="border p-2">
                        <button
                          onClick={() => handleSelectBuilding(b)}
                          className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {buildings.length === 0 && (
                  <tr>
                    <td colSpan={4} className="border p-2 text-center">
                      No buildings found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination controls */}
            <div className="flex items-center justify-between mt-4">
              <button
                disabled={page <= 0}
                onClick={handlePrev}
                className="px-3 py-1 bg-gray-300 text-gray-700 rounded disabled:bg-gray-100"
              >
                Prev
              </button>

              <span className="font-semibold text-gray-700">
                Page {page + 1} of {totalPages}
              </span>

              <button
                disabled={page >= totalPages - 1}
                onClick={handleNext}
                className="px-3 py-1 bg-gray-300 text-gray-700 rounded disabled:bg-gray-100"
              >
                Next
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BuildingLookupModal;
